import { DollarCircleFilledIcon, LocationFilledIcon, TimeFilledIcon } from '@backend/lib-design-system/icons';
import type { FC } from 'react';

import type { JobOfferWithJobPost } from '~/business/JobOffer/types';
import { BackBtn } from '~/components/BackBtn/backbtn';
import { Column } from '~/components/Column';
import { IconWrapper } from '~/components/MissionsList/MissionListCard.style';
import { PublishedDate } from '~/components/PublishedDate';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useTranslation } from '~/hooks';
import { getAreaCode } from '~/utils/areaCode';

import {
  GreyDot,
  HeaderCard,
  HeaderCardContainer,
  HeaderCardText,
  HeaderRow,
  MissionHeaderSubtitle,
  MissionHeaderTitle,
  TitleWrapper,
} from './styled';

export const MissionHeader: FC<JobOfferWithJobPost> = ({ jobOffer, jobPost }) => {
  const t = useTranslation();
  const { country } = useBusinessUnitContext();

  const areaCode = getAreaCode(country, jobOffer.location);

  return (
    <Column>
      <BackBtn />
      <TitleWrapper>
        <MissionHeaderTitle variant="heading-1">
          {t(`missions.mission_h1.${areaCode ? 'with_area' : 'without_area'}`, {
            title: jobOffer.title,
            city: jobOffer.location.city,
            areaCode,
          })}
        </MissionHeaderTitle>
        <MissionHeaderSubtitle variant="body">
          <PublishedDate publishedAt={jobPost.publishedAt} data-testid="job-offer-published-date" />
          <GreyDot />
          <span data-testid="job-offer-ref-id">{jobPost.refId}</span>
        </MissionHeaderSubtitle>
      </TitleWrapper>
      <HeaderRow>
        {jobOffer.salaryFormattedCurrency && jobOffer.salary?.unitRate && (
          <HeaderCard>
            <HeaderCardContainer>
              <IconWrapper $intent="warning">
                <DollarCircleFilledIcon size="l" intent="warning" />
              </IconWrapper>
              <HeaderCardText
                text={jobOffer.salaryFormattedCurrency}
                sub={t(`salary.gross_per_unit.${jobOffer.salary.unitRate.toLowerCase()}`)}
                size="s"
                textAs="p"
              />
            </HeaderCardContainer>
          </HeaderCard>
        )}
        {jobOffer.weeklyHours && (
          <HeaderCard>
            <HeaderCardContainer>
              <IconWrapper $intent="success">
                <TimeFilledIcon size="l" intent="success" />
              </IconWrapper>
              <HeaderCardText
                text={t('missions.weekly_hours.hours', { hours: jobOffer.weeklyHours })}
                sub={t('missions.weekly_hours.per_week')}
                size="s"
                textAs="p"
              />
            </HeaderCardContainer>
          </HeaderCard>
        )}
        <HeaderCard>
          <HeaderCardContainer>
            <IconWrapper $intent="primary">
              <LocationFilledIcon size="l" intent="primary" style={{ color: '#196CFF' }} />
            </IconWrapper>
            <HeaderCardText text={jobOffer.location.city} sub={jobOffer.location.postalCode} size="s" textAs="p" />
          </HeaderCardContainer>
        </HeaderCard>
      </HeaderRow>
    </Column>
  );
};
